/**
 * Low-flow / field-parameter stabilization check.
 *
 * Field tech captures pH, specific conductance, dissolved oxygen,
 * temperature, ORP, turbidity at intervals during purge. EPA Region 6
 * accepts stabilization when **three consecutive readings** all fall
 * within these tolerances of the previous reading:
 *
 *   pH                  ±0.10  SU
 *   spec. conductance   ±3%
 *   dissolved O2        ±10% (or ±0.3 mg/L for low values)
 *   temperature         ±0.5  °C
 *   ORP                 ±10   mV
 *   turbidity           ±10%  (informational; not blocking)
 *
 * The wizard's FieldParamsStep + PurgeStep (when low_flow) call
 * `isStabilized()` after the user adds a reading; the green ✓ banner
 * shows when the last three pass.
 */

export interface Reading {
  ph?: number | null;
  conductivity_us_cm?: number | null;
  dissolved_oxygen_mg_l?: number | null;
  temperature_c?: number | null;
  orp_mv?: number | null;
  turbidity_ntu?: number | null;
  /** Millis since epoch; arbitrary monotonic counter is fine. */
  recorded_at: number;
}

export interface StabilizationResult {
  /** True when the most recent N readings (default 3) are all within
   *  tolerance of the reading before them. */
  stabilized: boolean;
  /** Number of consecutive readings (including the latest) that satisfy
   *  the tolerance check. 0 if there's only one reading. */
  consecutive: number;
  /** Per-parameter pass/fail on the most recent vs. previous reading.
   *  Useful for highlighting which value the tech needs to wait on. */
  failingFields: Array<keyof Omit<Reading, 'recorded_at'>>;
}

const TOLERANCE_ABS: Partial<Record<keyof Reading, number>> = {
  ph: 0.10,
  temperature_c: 0.5,
  orp_mv: 10,
};
/** Percent tolerance applied to the PREVIOUS value (so ±3% of prior cond). */
const TOLERANCE_PCT: Partial<Record<keyof Reading, number>> = {
  conductivity_us_cm: 0.03,
  dissolved_oxygen_mg_l: 0.10,
  turbidity_ntu: 0.10,
};

function withinTolerance(field: keyof Reading, prev: number, cur: number): boolean {
  const abs = TOLERANCE_ABS[field];
  if (abs != null) return Math.abs(cur - prev) <= abs;
  const pct = TOLERANCE_PCT[field];
  if (pct != null) {
    const window = Math.abs(prev) * pct;
    return Math.abs(cur - prev) <= window;
  }
  // No tolerance configured → treat as informational (always passes).
  return true;
}

function diffFailing(prev: Reading, cur: Reading): Array<keyof Omit<Reading, 'recorded_at'>> {
  const fields: Array<keyof Omit<Reading, 'recorded_at'>> = [
    'ph', 'conductivity_us_cm', 'dissolved_oxygen_mg_l', 'temperature_c', 'orp_mv',
  ];
  const out: Array<keyof Omit<Reading, 'recorded_at'>> = [];
  for (const f of fields) {
    const p = prev[f];
    const c = cur[f];
    if (p == null || c == null) continue;
    if (!withinTolerance(f, p, c)) out.push(f);
  }
  return out;
}

/** Return stabilization status given the ordered list of readings (oldest first).
 *  `requiredConsecutive` defaults to 3 (EPA Region 6 standard). */
export function isStabilized(
  readings: Reading[],
  requiredConsecutive = 3,
): StabilizationResult {
  if (readings.length < 2) {
    return { stabilized: false, consecutive: readings.length, failingFields: [] };
  }
  let consecutive = 1;
  let lastFailing: Array<keyof Omit<Reading, 'recorded_at'>> = [];
  // Count from the end backward: how many sequential pairs are within tolerance?
  for (let i = readings.length - 1; i > 0; i--) {
    const failing = diffFailing(readings[i - 1], readings[i]);
    if (failing.length === 0) {
      consecutive += 1;
    } else {
      if (i === readings.length - 1) lastFailing = failing;
      break;
    }
  }
  return {
    stabilized: consecutive >= requiredConsecutive,
    consecutive,
    failingFields: lastFailing,
  };
}

/** Field-display labels for the failing-fields chip list. */
export const READING_FIELDS: Array<{
  key: keyof Omit<Reading, 'recorded_at'>;
  label: string;
  unit: string;
  tolerance: string;
  /** Step for the numeric input. */
  step: string;
}> = [
  { key: 'ph',                    label: 'pH',       unit: 'SU',    tolerance: '±0.10',  step: '0.01' },
  { key: 'conductivity_us_cm',    label: 'Cond.',    unit: 'µS/cm', tolerance: '±3%',    step: '1' },
  { key: 'dissolved_oxygen_mg_l', label: 'DO',       unit: 'mg/L',  tolerance: '±10%',   step: '0.01' },
  { key: 'temperature_c',         label: 'Temp.',    unit: '°C',    tolerance: '±0.5',   step: '0.1' },
  { key: 'orp_mv',                label: 'ORP',      unit: 'mV',    tolerance: '±10',    step: '1' },
  { key: 'turbidity_ntu',         label: 'Turbidity',unit: 'NTU',   tolerance: '—',      step: '0.1' },
];
